// "How this is judged": every threshold, the worst value the forecast being
// shown reaches against each, and how far that is from amber — the answer to
// "it looks grim, so why isn't it amber, and when would it be?". Then how the
// overall severity, the refresh schedule and the change commentary follow from
// them. All of it is read from GET /safety/config (app/safety/rules.py), so it
// can't describe rules other than the ones in force.
import {
  Box, Link, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import SafetySection from "./SafetySection";
import { describeProfile } from "./ScheduleDialog";
import {
  BAND_LABEL, SEVERITY_LABEL, fmtMetric, fmtStepTime, profileLabel,
} from "./format";

const RANK = { none: 0, watch: 1, amber: 2, red: 3 };

// The worst value of one metric across the fetch's points, and where.
function worstAcross(summary, metric) {
  let best = null;
  for (const p of summary?.points || []) {
    const v = p.metrics?.[metric.field];
    if (!p.in_range || !v) continue;
    const worse = !best || (metric.direction === "below" ? v.value < best.value : v.value > best.value);
    if (worse) best = { ...v, point: p.name };
  }
  return best;
}

function thresholdText(metric, t) {
  if (t === undefined || t === null) return "—";
  return `${metric.direction === "below" ? "≤" : "≥"} ${fmtMetric(metric, t)}`;
}

function marginText(metric, worst) {
  if (!worst) return "Not in the forecast";
  if (RANK[worst.band] >= RANK.amber) return BAND_LABEL[worst.band];
  const gap = metric.direction === "below" ? worst.value - metric.amber : metric.amber - worst.value;
  const by = fmtMetric(metric, Math.round(gap * 10) / 10);
  return `${by} ${metric.direction === "below" ? "above" : "below"} amber`;
}

export default function RulesPanel({ config, run }) {
  const summary = run?.summary;
  const sev = config.recommended_profile || {};
  return (
    <SafetySection id="rules" title="How this is judged" defaultCollapsed
      summary={<Typography variant="body2" color="text.secondary">
        The thresholds behind the bands, how close the forecast is to each, and how the refresh
        schedule and change commentary follow from them.
      </Typography>}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>The thresholds</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Each point is judged on the worst value of each measure during its own window — when the
            leaders to the back of the field are there, for a point on a route, otherwise the
            assessment's whole window. Only the Met Office forecast is judged; Windy is a comparison
            and MWIS is for reading. A hazard takes the worst of its measures. These are beta
            defaults, not Shelter Stone policy, and live in <code>app/safety/rules.py</code>.
            {run && <> The last two columns are for the fetch of {fmtStepTime(run.fetched_at)}.</>}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Hazard</TableCell>
                  <TableCell>Measure</TableCell>
                  <TableCell>Approaching amber</TableCell>
                  <TableCell>Amber</TableCell>
                  <TableCell>Red</TableCell>
                  <TableCell>Worst in the forecast</TableCell>
                  <TableCell>Distance from amber</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {config.hazards.map(h => h.metrics.map((m, i) => {
                  const worst = summary ? worstAcross(summary, m) : null;
                  return (
                    <TableRow key={m.field}>
                      <TableCell sx={{ fontWeight: 600 }}>{i === 0 ? h.label : ""}</TableCell>
                      <TableCell>{m.label}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{thresholdText(m, m.watch)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{thresholdText(m, m.amber)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{thresholdText(m, m.red)}</TableCell>
                      <TableCell>
                        {worst ? (
                          <>
                            {fmtMetric(m, worst.value)}
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {worst.point}, {fmtStepTime(worst.time)}
                            </Typography>
                          </>
                        ) : "—"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: worst && RANK[worst.band] >= RANK.amber ? 700 : undefined }}>
                        {summary ? marginText(m, worst) : "—"}
                      </TableCell>
                    </TableRow>
                  );
                }))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            "≥" measures reach a band at or above the figure; "≤" ones (cold, visibility) at or below
            it. A measure a provider doesn't forecast isn't judged — the Met Office hourly forecast has
            no lightning chance of its own, so each hour borrows its three-hourly step's.
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Overall severity</Typography>
          <Typography variant="body2">
            An assessment is as severe as its worst point: <strong>Red</strong> or <strong>Amber</strong> when
            any measure at any point reaches that band in its window; <strong>Approaching amber</strong> when
            something is past its approaching level but nothing is amber; <strong>Benign</strong> when every
            measure everywhere is short of approaching. Approaching amber changes no band on the forecast
            tables — it exists to tell "well below amber" from "just under it", for the schedule and the
            commentary. Points whose window is beyond the forecast's range aren't counted; if none is in
            range yet, there is no severity.
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Refresh schedule</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            With auto-refresh on, forecasts are fetched on a schedule counted back from the window's
            start, getting more frequent as it approaches and stopping at the start. Left on
            "recommended", the schedule is re-chosen after every fetch from the severity:
          </Typography>
          <Table size="small" sx={{ maxWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Schedule</TableCell>
                <TableCell>Fetches</TableCell>
                <TableCell>Recommended when</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {config.refresh_profiles.map(p => {
                const when = Object.entries(sev).filter(([, k]) => k === p.key)
                  .map(([s]) => (s === "unknown" ? "nothing in range yet" : SEVERITY_LABEL[s]?.toLowerCase()));
                return (
                  <TableRow key={p.key}>
                    <TableCell sx={{ fontWeight: 600 }}>{profileLabel(config, p.key)}</TableCell>
                    <TableCell>{describeProfile(p)}</TableCell>
                    <TableCell>{when.length ? when.join(", ") : "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            A fetch within {config.schedule?.min_gap_minutes} minutes of a scheduled one — a manual one,
            say — stands in for it. Scheduled fetches leave MWIS out when the stored one is less than{" "}
            {config.schedule?.mwis_min_hours} hours old, since it's published about once a day.
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>What counts as a change</Typography>
          <Typography variant="body2">
            Each fetch is compared with the one before it, point by point and measure by measure, on the
            worst value in the window. A change is mentioned when a measure crosses into a different band,
            or when it moves by at least its "significant" amount while at or beyond approaching amber —
            drift well below the thresholds isn't news. The verdict is <em>worse</em> or <em>better</em> when
            every mentioned change goes one way, <em>mixed</em> when they go both ways, and <em>much the
            same</em> when nothing is worth mentioning.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Significant amounts:{" "}
            {config.hazards.flatMap(h => h.metrics).filter(m => m.significant != null)
              .map(m => `${m.label.toLowerCase()} ${fmtMetric(m, m.significant)}`).join(", ")}.
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary">
          Route timings (when the leaders and the back of the field reach each point) come from a pace
          model: distance plus 1 km per 100 m of climb, scaled to the expected winning time and the
          course close — see the Routes section. More on the forecast sources:{" "}
          <Link href="https://www.metoffice.gov.uk/services/data/met-office-weather-datahub" target="_blank" rel="noopener">Met Office DataHub</Link>,{" "}
          <Link href="https://www.mwis.org.uk/" target="_blank" rel="noopener">MWIS</Link>.
        </Typography>
      </Stack>
    </SafetySection>
  );
}
